import { apiErrorMessage, customFetch } from '@agos/api-client'
import {
  getGetLunchTemplatesQueryKey,
  useDeleteLunchTemplatesId,
  useGetLunchTemplates,
  usePatchLunchTemplatesId,
  usePostLunchTemplates,
} from '@agos/api-client/lunch-template-controller'
import { useGetProducts } from '@agos/api-client/menu'
import { T, formatMoney, tt } from '@agos/shared'
import {
  BUTTON_SECONDARY,
  cn,
  ConfirmDialog,
  PageHeader,
  toast,
} from '@agos/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Copy,
  Loader2,
  Plus,
  Printer,
  Save,
  Trash2,
  Utensils,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { LunchPrintView, sectionLabel } from './LunchPrintView'
import {
  ALL_WEEKDAYS,
  CUSTOM_SECTION_PRICE,
  DEFAULT_LUNCH_SETTINGS,
  LUNCH_ALLERGENS,
  allLunchSections,
  customLunchSections,
  customSectionKey,
  dayKeyOf,
  dayShortDate,
  emptyWeek,
  weekFrom,
  formatShortDate,
  lunchMealDescription,
  mondayOf,
  normaliseLunchSettings,
  sectionNameProblem,
  sundayOf,
  type LunchMeal,
  type LunchSettings,
  type LunchWeek,
} from '@agos/shared'

/**
 * Ported from legacy `pages/lunch-menu.tsx` + `lib/lunch-store.ts`. Spec:
 * `docs/pages/manager-menu/lunch-menu.md`.
 *
 * Settings and every week are stored as one blob at `/settings/lunch`, which
 * the backend does not describe — so the shape lives in `lunch-week.ts`, as it
 * does in legacy.
 */

const FIELD_CLASSES =
  'w-full px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'
const SAVED_BADGE_MS = 2000

interface LunchPayload {
  settings?: Partial<LunchSettings>
  weeks?: LunchWeek[]
}

function newMeal(type: string, defaultPrice: number): LunchMeal {
  return {
    id: `meal_${crypto.randomUUID()}`,
    type,
    name: '',
    price: defaultPrice,
    allergens: [],
  }
}

/**
 * What to say when a linked product's catalogue price is not the meal's menu price — §9.39.
 *
 * Returns null when there is nothing to say: no link, no product found, or the two agree. A menu
 * price below the catalogue one is the ordinary case and still says so, because the number is the
 * point — 4,90 € against 8,90 € reads as a daily-menu discount, and 4,90 € against 49,00 € reads as
 * a typo, and only the reader can tell which.
 */
/** One row of `GET /lunch-templates`, read defensively because it arrives untyped. */
interface TemplateRow {
  id?: number
  name?: string
  isDefault?: boolean
  days?: unknown
}

/**
 * Moves a meal one place up or down the day's list — §9.133, the owner's point 17.
 *
 * <p><b>The order is not cosmetic.</b> `sectionsFor` groups by type and keeps array order inside each
 * group, so this decides the sequence on the customer's menu, on the printed sheet and in the
 * manager's own lunch tab.
 *
 * <p><b>A plain adjacent swap, and legacy's same-type guard is deliberately not copied.</b> Legacy
 * refuses a move between two meals of different types because its editor is grouped by section, so
 * the row would jump into a section it is not in. This editor is a flat list with a section dropdown
 * on every row: the array order is exactly what is drawn, so swapping with the neighbour moves the
 * row where the person can watch it go. The guard would make the button do nothing on a boundary row
 * for no visible reason.
 *
 * <p>Returns the list unchanged when there is nowhere to go, so the caller has nothing to special-case
 * and a no-op cannot mark the week dirty.
 */
export function moveMeal<T extends { id: string }>(
  meals: readonly T[],
  mealId: string,
  direction: -1 | 1,
): T[] {
  const from = meals.findIndex((meal) => meal.id === mealId)
  const to = from + direction
  if (from < 0 || to < 0 || to >= meals.length) return [...meals]
  const moved = [...meals]
  const [taken] = moved.splice(from, 1)
  if (taken === undefined) return [...meals]
  moved.splice(to, 0, taken)
  return moved
}

export function priceGapNote(
  meal: { productId?: number; price: number | string },
  products: readonly { id?: number; name?: string; price?: unknown }[],
): string | null {
  if (meal.productId === undefined || meal.productId <= 0) return null
  const product = products.find((candidate) => candidate.id === meal.productId)
  if (product === undefined) return null
  const catalogue = Number(product.price)
  const menu = Number(meal.price)
  if (!Number.isFinite(catalogue) || !Number.isFinite(menu) || catalogue === menu) return null
  return `${tt('V cenníku')} ${formatMoney(catalogue)} - ${tt('menu cena')} ${formatMoney(menu)}. ${tt('Účtuje sa menu cena; produkt slúži na odpis skladu.')}`
}

export function LunchMenuPage() {
  // §9.39 — the catalogue, for the stock link. Read-only here; the picker writes only an id.
  const queryClient = useQueryClient()
  const { data: productData } = useGetProducts({})
  const linkableProducts = useMemo(
    () =>
      // §9.401 - `description` rides along, because a meal's popis is its linked product's.
      ((productData ?? []) as Array<{
        id?: number
        name?: string
        price?: unknown
        description?: string | null
      }>)
        .filter((product) => product.id !== undefined && (product.name ?? '').trim() !== '')
        .sort((left, right) => (left.name ?? '').localeCompare(right.name ?? '', 'sk')),
    [productData],
  )

  const [settings, setSettings] = useState<LunchSettings>(DEFAULT_LUNCH_SETTINGS)
  const [weeks, setWeeks] = useState<LunchWeek[]>([])
  const [weekIndex, setWeekIndex] = useState(0)
  const [savedSnapshot, setSavedSnapshot] = useState('')
  const [justSaved, setJustSaved] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [printMode, setPrintMode] = useState<'week' | 'day'>('week')
  const [pendingWeekDeletion, setPendingWeekDeletion] = useState<LunchWeek | null>(null)
  /** §9.315 - the name being typed for a new section, and the one queued for deletion. */
  const [newSectionName, setNewSectionName] = useState('')
  const [sectionProblem, setSectionProblem] = useState('')
  const [pendingSectionDeletion, setPendingSectionDeletion] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const response = await customFetch<LunchPayload | null>('/api/v1/settings/lunch')
        if (cancelled) return

        const loadedSettings = normaliseLunchSettings(response?.settings)
        const thisMonday = mondayOf(new Date())
        const loadedWeeks =
          response?.weeks && response.weeks.length > 0
            ? response.weeks
            : [emptyWeek(thisMonday, `${formatShortDate(thisMonday)} – ${formatShortDate(sundayOf(thisMonday))}`)]

        setSettings(loadedSettings)
        setWeeks(loadedWeeks)
        setSavedSnapshot(JSON.stringify({ settings: loadedSettings, weeks: loadedWeeks }))
        // Open on the current week if it exists, as legacy does.
        const currentIndex = loadedWeeks.findIndex((week) => week.startDate === thisMonday)
        setWeekIndex(currentIndex === -1 ? 0 : currentIndex)
      } catch {
        // An unreachable backend still gets an editable empty week.
        const thisMonday = mondayOf(new Date())
        if (!cancelled) setWeeks([emptyWeek(thisMonday, formatShortDate(thisMonday))])
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const saveLunch = useMutation({
    mutationFn: (payload: { settings: LunchSettings; weeks: LunchWeek[] }) =>
      customFetch<unknown>('/api/v1/settings/lunch', {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
  })

  const hasUnsavedChanges = JSON.stringify({ settings, weeks }) !== savedSnapshot

  // Same shape as FB-020 and FB-025: a whole week of meals typed in, lost on a
  // stray click, because nothing is written until Uložiť.
  useEffect(() => {
    if (!hasUnsavedChanges) return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [hasUnsavedChanges])

  const week = weeks[weekIndex]

  /**
   * FB-035: legacy filters the week's *stored* days, so switching "Zobraziť
   * víkendy" on a week that was saved without a Saturday and Sunday does
   * nothing at all — the days are not there to reveal. The list is derived from
   * the canonical week instead, and a day the week has never held appears empty
   * and disabled, ready to fill in.
   */
  const visibleDays = useMemo(() => {
    const storedByKey = new Map((week?.days ?? []).map((day) => [day.dayKey, day]))
    return ALL_WEEKDAYS.filter(
      (weekday) => settings.showWeekends || (weekday.key !== 'sat' && weekday.key !== 'sun'),
    ).map(
      (weekday) =>
        storedByKey.get(weekday.key) ?? {
          dayKey: weekday.key,
          dayName: weekday.name,
          enabled: false,
          meals: [],
        },
    )
  }, [week, settings.showWeekends])

  /** §9.315 - every section a meal may be filed under, the built-ins first. */
  const sectionOptions = useMemo(() => allLunchSections(settings), [settings])

  /**
   * How many meals across **every** week sit in this section, which is what the delete confirmation
   * has to say. Counting the open week only would promise a smaller loss than the click causes.
   */
  const mealsInSection = (type: string): number =>
    weeks.reduce(
      (total, entry) =>
        total +
        entry.days.reduce(
          (dayTotal, day) => dayTotal + day.meals.filter((meal) => meal.type === type).length,
          0,
        ),
      0,
    )

  const addCustomSection = () => {
    const label = newSectionName.trim()
    if (label === '') return
    // Legacy allows a duplicate; §9.315 refuses one, because two identical headings print twice and
    // nothing in the editor can then tell them apart to undo it.
    // §9.398 - which section holds the name, because the owner's own list held one entry and the
    // name they typed belonged to a built-in they could not see in it.
    const clash = sectionNameProblem(settings, label)
    if (clash !== '') {
      setSectionProblem(clash)
      return
    }
    setSectionProblem('')
    setSettings((current) => ({
      ...current,
      customSections: [
        ...customLunchSections(current.customSections),
        { type: customSectionKey(Date.now()), label, defaultPrice: CUSTOM_SECTION_PRICE },
      ],
    }))
    setNewSectionName('')
  }

  const removeCustomSection = (type: string) => {
    setSettings((current) => ({
      ...current,
      customSections: customLunchSections(current.customSections).filter(
        (section) => section.type !== type,
      ),
      // The label override goes with it, or a section created later under the same key would inherit
      // a name nobody typed.
      sectionLabels: Object.fromEntries(
        Object.entries(current.sectionLabels ?? {}).filter(([key]) => key !== type),
      ),
    }))
    // Legacy drops the meals filed under it, and so does this - a meal whose section is gone has no
    // heading to print under. Every week, not just the open one.
    setWeeks((current) =>
      current.map((entry) => ({
        ...entry,
        days: entry.days.map((day) => ({
          ...day,
          meals: day.meals.filter((meal) => meal.type !== type),
        })),
      })),
    )
  }

  const patchWeek = (patch: Partial<LunchWeek>) =>
    setWeeks((current) =>
      current.map((entry, index) => (index === weekIndex ? { ...entry, ...patch } : entry)),
    )

  const patchDay = (dayKey: string, patch: Partial<LunchWeek['days'][number]>) => {
    const days = week?.days ?? []
    if (days.some((day) => day.dayKey === dayKey)) {
      patchWeek({ days: days.map((day) => (day.dayKey === dayKey ? { ...day, ...patch } : day)) })
      return
    }
    // The day was only ever shown, never stored — add it now that it is edited.
    const weekday = ALL_WEEKDAYS.find((entry) => entry.key === dayKey)
    patchWeek({
      days: [
        ...days,
        { dayKey, dayName: weekday?.name ?? dayKey, enabled: false, meals: [], ...patch },
      ],
    })
  }

  const patchMeal = (dayKey: string, mealId: string, patch: Partial<LunchMeal>) => {
    const day = week?.days.find((entry) => entry.dayKey === dayKey)
    if (!day) return
    patchDay(dayKey, {
      meals: day.meals.map((meal) => (meal.id === mealId ? { ...meal, ...patch } : meal)),
    })
  }

  /**
   * The Monday after the last week on file.
   *
   * From the **last** week rather than the one on screen, so adding twice in a row does not try to
   * create the same week twice.
   */
  const nextMondayAfterLast = () => {
    const lastStart = weeks[weeks.length - 1]?.startDate ?? mondayOf(new Date())
    const [year, month, day] = lastStart.split('-').map(Number)
    return mondayOf(new Date(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + 7))
  }

  const appendWeek = (build: (start: string, label: string) => LunchWeek) => {
    const start = nextMondayAfterLast()
    setWeeks((current) => [
      ...current,
      build(start, `${formatShortDate(start)} – ${formatShortDate(sundayOf(start))}`),
    ])
    setWeekIndex(weeks.length)
  }

  /**
   * Next week, starting from the one on screen — the owner's request, and the button that should be
   * reached for. `weekFrom` mints new meal ids; see its header for why that matters rather than being
   * tidiness.
   */
  const copyWeek = () => {
    const source = weeks[weekIndex]
    if (source === undefined) return
    appendWeek((start, label) => weekFrom(source, start, label))
  }

  const addWeek = () => {
    appendWeek(emptyWeek)
  }

  // §10A.18 — the named weeks a week can be filled from, and what the nightly job reads.
  const { data: templateData } = useGetLunchTemplates()
  const templates = useMemo(
    () => (Array.isArray(templateData) ? (templateData as TemplateRow[]) : []),
    [templateData],
  )
  const [templateName, setTemplateName] = useState('')
  const [templateBusy, setTemplateBusy] = useState(false)
  const saveTemplate = usePostLunchTemplates()
  const patchTemplate = usePatchLunchTemplatesId()
  const dropTemplate = useDeleteLunchTemplatesId()

  const refreshTemplates = () =>
    void queryClient.invalidateQueries({ queryKey: getGetLunchTemplatesQueryKey() })

  /**
   * Save the week on screen as a template.
   *
   * The days go verbatim — a template's shape is a week's shape (§10A.18) — and the week's own
   * `startDate` and `label` are deliberately left behind, because a template carrying dates would be
   * valid for exactly one week.
   */
  const storeTemplate = async () => {
    if (week === undefined || templateName.trim() === '') return
    setTemplateBusy(true)
    try {
      await saveTemplate.mutateAsync({
        data: { name: templateName.trim(), days: week.days as never },
      })
      setTemplateName('')
      refreshTemplates()
      toast({ title: tt('Šablóna uložená.') })
    } catch (failure) {
      toast({
        variant: 'destructive',
        title: apiErrorMessage(failure, tt('Šablónu sa nepodarilo uložiť.')),
      })
    } finally {
      setTemplateBusy(false)
    }
  }

  /**
   * Fill the week on screen from a template.
   *
   * Written into the editor's own state rather than through the endpoint, so it lands beside every
   * other unsaved edit and the manager still presses Uložiť — applying a template is a draft, and a
   * screen that writes to the server behind the save button is one nobody can undo.
   */
  const applyTemplate = (template: TemplateRow) => {
    if (week === undefined || !Array.isArray(template.days)) return
    setWeeks((current) =>
      current.map((candidate, index) =>
        index === weekIndex
          ? { ...candidate, days: JSON.parse(JSON.stringify(template.days)) as LunchWeek['days'] }
          : candidate,
      ),
    )
    toast({ title: `${tt('Týždeň vyplnený zo šablóny')} „${template.name}".` })
  }

  const save = () =>
    saveLunch.mutate(
      { settings, weeks },
      {
        onSuccess: () => {
          setSavedSnapshot(JSON.stringify({ settings, weeks }))
          setJustSaved(true)
          setTimeout(() => setJustSaved(false), SAVED_BADGE_MS)
        },
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Uloženie zlyhalo'),
            description: apiErrorMessage(caught, tt('Obedové menu sa nepodarilo uložiť.')),
          }),
      },
    )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!week) return null

  return (
    <div className="space-y-6">
      <div className="no-print space-y-6">
        <PageHeader
          title={tt('Obedové menu')}
          description={tt('Týždenné obedové menu pre web aj na vytlačenie.')}
          icon={<Utensils className="w-5 h-5" />}
          actions={
            <>
              {hasUnsavedChanges && (
                <span className="text-xs font-semibold text-amber-500 whitespace-nowrap">
                  <T>Neuložené zmeny</T>
                </span>
              )}
              <select
                value={printMode}
                onChange={(event) => setPrintMode(event.target.value === 'day' ? 'day' : 'week')}
                aria-label={tt('Rozsah tlače')}
                className={cn(FIELD_CLASSES, 'w-auto')}
              >
                <option value="week">{tt('Tlačiť týždeň')}</option>
                <option value="day">{tt('Tlačiť dnešný deň')}</option>
              </select>
              <button
                type="button"
                onClick={() => window.print()}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border font-bold text-sm hover:bg-muted/50 transition-colors"
              >
                <Printer className="w-4 h-4" /> <T>Tlačiť</T>
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saveLunch.isPending}
                className={cn(
                  'flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all disabled:opacity-50',
                  justSaved
                    ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                    : 'bg-primary text-primary-foreground font-bold hover:opacity-90 transition-opacity disabled:opacity-50',
                )}
              >
                {saveLunch.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {justSaved ? tt('Uložené!') : tt('Uložiť')}
              </button>
            </>
          }
        />

        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={tt('Predchádzajúci týždeň')}
            disabled={weekIndex === 0}
            onClick={() => setWeekIndex((index) => Math.max(0, index - 1))}
            className="p-2 rounded-lg border border-border disabled:opacity-40 hover:bg-muted/50"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 text-center">
            <p className="font-bold text-foreground">{week.label}</p>
            {week.startDate && (
              <p className="text-xs text-muted-foreground">
                {week.startDate} - {sundayOf(week.startDate)}
                {week.startDate === mondayOf(new Date()) && (
                  <span className="ml-2 text-primary font-semibold">
                    <T>tento týždeň</T>
                  </span>
                )}
              </p>
            )}
          </div>
          <button
            type="button"
            aria-label={tt('Nasledujúci týždeň')}
            disabled={weekIndex >= weeks.length - 1}
            onClick={() => setWeekIndex((index) => Math.min(weeks.length - 1, index + 1))}
            className="p-2 rounded-lg border border-border disabled:opacity-40 hover:bg-muted/50"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={copyWeek}
            data-week-copy
            disabled={week === undefined}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary/10 text-primary border border-primary/20 text-xs font-bold hover:bg-primary/20 disabled:opacity-40"
          >
            <Copy className="w-3.5 h-3.5" /> <T>Nový týždeň z tohto</T>
          </button>
          {/* Still reachable, because the first week of a new section of the menu has nothing to copy
              - but it is no longer the button somebody hits every Friday. */}
          <button
            type="button"
            onClick={addWeek}
            data-week-empty
            className={cn(BUTTON_SECONDARY, 'flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs')}
          >
            <Plus className="w-3.5 h-3.5" /> <T>Prázdny týždeň</T>
          </button>
          {weeks.length > 1 && (
            <button
              type="button"
              aria-label={tt('Zmazať týždeň')}
              onClick={() => setPendingWeekDeletion(week)}
              className="p-2 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* §10A.18 - named weeks. *Uložiť ako šablónu* keeps the week on screen under a name;
          * *Použiť* fills the week from one. The default is what the nightly job reads at 03:30, and
          * a franchise with none set gets no automatic weeks at all — which is why the star says
          * plainly what it does rather than being a bare toggle. */}
        <div className="rounded-2xl border border-border bg-card p-4 space-y-3" data-lunch-templates>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="font-bold text-sm">
                <T>Šablóny týždňa</T>
              </p>
              <p className="text-[11px] text-muted-foreground">
                <T>Označená šablóna sa každú noc použije na chýbajúce týždne (2 týždne dopredu).</T>
              </p>
            </div>
            <div className="flex gap-2">
              <input
                value={templateName}
                onChange={(event) => setTemplateName(event.target.value)}
                placeholder={tt('Názov šablóny')}
                aria-label={tt('Názov šablóny')}
                data-template-name
                className={cn(FIELD_CLASSES, 'w-48')}
              />
              <button
                type="button"
                data-template-save
                disabled={templateBusy || templateName.trim() === '' || week === undefined}
                onClick={() => void storeTemplate()}
                className="px-3 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-bold hover:opacity-90 disabled:opacity-50"
              >
                <T>Uložiť ako šablónu</T>
              </button>
            </div>
          </div>

          {templates.length === 0 ? (
            <p className="text-[11px] text-muted-foreground" data-templates-empty>
              <T>
                Zatiaľ žiadna šablóna. Kým nejakú neoznačíte ako predvolenú, nočná úloha nevytvorí
                nič.
              </T>
            </p>
          ) : (
            <div className="rounded-xl border border-border divide-y divide-border">
              {templates.map((template) => (
                <div
                  key={template.id}
                  data-lunch-template={template.name}
                  className="flex items-center justify-between gap-2 px-3 py-2"
                >
                  <span className="text-sm truncate flex-1">
                    {template.name}
                    {template.isDefault === true && (
                      <span className="ml-2 text-[10px] font-bold text-primary uppercase">
                        <T>predvolená</T>
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    data-template-apply={template.name}
                    disabled={week === undefined}
                    onClick={() => applyTemplate(template)}
                    className="shrink-0 text-xs font-bold text-primary hover:underline disabled:opacity-40"
                  >
                    <T>Použiť</T>
                  </button>
                  {template.isDefault !== true && (
                    <button
                      type="button"
                      data-template-default={template.name}
                      disabled={templateBusy || template.id === undefined}
                      onClick={() => {
                        setTemplateBusy(true)
                        patchTemplate.mutate(
                          { id: template.id as number, data: { isDefault: true } },
                          {
                            // The owner set a template as default and saw nothing change: this had
                            // `onSettled` and no `onError`, so a refused PATCH cleared the spinner,
                            // refreshed a list that had not changed, and said nothing at all. The
                            // save above this has always had one.
                            onError: (caught) =>
                              toast({
                                variant: 'destructive',
                                title: tt('Nepodarilo sa nastaviť predvolenú'),
                                description: apiErrorMessage(
                                  caught,
                                  tt('Šablónu sa nepodarilo nastaviť ako predvolenú.'),
                                ),
                              }),
                            onSettled: () => {
                              setTemplateBusy(false)
                              refreshTemplates()
                            },
                          },
                        )
                      }}
                      className="shrink-0 text-xs font-bold text-muted-foreground hover:text-foreground disabled:opacity-40"
                    >
                      <T>Nastaviť predvolenú</T>
                    </button>
                  )}
                  <button
                    type="button"
                    data-template-delete={template.name}
                    aria-label={tt('Zmazať šablónu')}
                    disabled={templateBusy || template.id === undefined}
                    onClick={() => {
                      setTemplateBusy(true)
                      dropTemplate.mutate(
                        { id: template.id as number },
                        {
                          onSettled: () => {
                            setTemplateBusy(false)
                            refreshTemplates()
                          },
                        },
                      )
                    }}
                    className="shrink-0 p-1 text-muted-foreground hover:text-red-500 disabled:opacity-40"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.showWeekends}
            onChange={(event) =>
              setSettings((current) => ({ ...current, showWeekends: event.target.checked }))
            }
            className="w-4 h-4 rounded accent-primary"
          />
          <span className="text-sm">
            <T>Zobraziť víkendy</T>
          </span>
        </label>

        {/* §9.399 - the owner: *"another button where we can DISABLE obedové menu completely, some
          * of the Prevádzky does not use that feature"*. `LunchSettings.visible` has existed since
          * the port and **no screen set it**: the customer app has read it the whole time. Worded as
          * what it does rather than as a visibility hint, because it is the only control that turns
          * the section off. */}
        <label className="flex items-center gap-2 cursor-pointer" data-lunch-visible-label>
          <input
            type="checkbox"
            data-lunch-visible
            checked={settings.visible}
            onChange={(event) =>
              setSettings((current) => ({ ...current, visible: event.target.checked }))
            }
            className="w-4 h-4 rounded accent-primary"
          />
          <span className="text-sm">
            <T>Obedové menu na webe</T>
          </span>
        </label>
        {!settings.visible && (
          <p className="text-[11px] text-muted-foreground" data-lunch-hidden-note>
            <T>
              Zákazníci obedové menu nevidia. Týždeň môžete ďalej pripravovať a tlačiť - na web sa
              vráti, keď to znova zapnete.
            </T>
          </p>
        )}

        {/* §9.39 - the franchise default for a soup taken with a main. `bundlePriceCentsOf` has read
          * this and charged from it since the customer's lunch basket was built; nothing could set
          * it, so a restaurant was charging a bundled price it had never chosen. A soup can still
          * override it on its own row. */}
        <label className="flex items-center gap-2 text-sm">
          <T>Polievka k menu</T>
          <input
            type="number"
            step="0.10"
            min="0"
            value={settings.soupBundlePrice ?? ''}
            placeholder={tt('napr. 0,50')}
            onChange={(event) =>
              setSettings((current) => ({
                ...current,
                // Cleared is *no franchise default*, which is what `bundlePriceCentsOf` reads as
                // "charge the soup's own price" — not as free.
                soupBundlePrice:
                  event.target.value.trim() === '' ? undefined : Number(event.target.value) || 0,
              }))
            }
            aria-label={tt('Polievka k menu')}
            data-soup-bundle-price
            className={cn(FIELD_CLASSES, 'w-28')}
          />
        </label>

        {/* §9.315, the owner's Q1 - *"categories can be also added not only from categories list, but
          * also custom categories… like we have now «Lokša»"*. `customSections` had been in the type
          * and read by the customer's lunch modal and the printed sheet since the port began, with no
          * screen able to write it. A custom section has no catalogue category, which is the whole
          * point of one: it takes any product, or none. */}
        <div className="space-y-2" data-custom-sections>
          <p className="text-sm font-bold">
            <T>Vlastné sekcie</T>
          </p>
          <p className="text-xs text-muted-foreground">
            <T>
              Sekcia, ktorá nie je kategóriou v ponuke - napríklad Lokša. Objaví sa v editore, v
              obedovom menu pre zákazníka aj na tlačenom liste.
            </T>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={newSectionName}
              onChange={(event) => {
                setNewSectionName(event.target.value)
                setSectionProblem('')
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  addCustomSection()
                }
              }}
              placeholder={tt('Názov novej sekcie')}
              aria-label={tt('Názov novej sekcie')}
              data-new-section-name
              className={cn(FIELD_CLASSES, 'w-52')}
            />
            <button
              type="button"
              data-add-section
              disabled={newSectionName.trim() === ''}
              onClick={addCustomSection}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus className="w-3.5 h-3.5" /> <T>Pridať sekciu</T>
            </button>
          </div>
          {sectionProblem !== '' && (
            <p className="text-xs text-red-500" data-section-problem>
              {sectionProblem}
            </p>
          )}
          {customLunchSections(settings.customSections).length === 0 ? (
            <p className="text-xs text-muted-foreground" data-no-custom-sections>
              <T>Zatiaľ žiadna vlastná sekcia.</T>
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {customLunchSections(settings.customSections).map((section) => (
                <span
                  key={section.type}
                  data-custom-section={section.type}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-2.5 py-1 text-xs font-semibold"
                >
                  {settings.sectionLabels?.[section.type] ?? section.label}
                  <span className="text-muted-foreground" data-custom-section-count>
                    ({mealsInSection(section.type)})
                  </span>
                  <button
                    type="button"
                    data-remove-section={section.type}
                    aria-label={tt('Odstrániť sekciu')}
                    onClick={() => {
                      // Nothing filed under it is nothing to lose, so no confirmation is asked for.
                      if (mealsInSection(section.type) === 0) removeCustomSection(section.type)
                      else setPendingSectionDeletion(section.type)
                    }}
                    className="text-muted-foreground hover:text-red-500"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          {visibleDays.map((day) => (
            <div key={day.dayKey} className="bg-card border border-border rounded-2xl overflow-hidden">
              <div className="px-5 py-3 bg-muted/40 border-b border-border flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={day.enabled}
                    onChange={(event) => patchDay(day.dayKey, { enabled: event.target.checked })}
                    aria-label={day.dayName}
                    className="w-4 h-4 rounded accent-primary"
                  />
                  {/* §9.386 - named for the tests that ask *is the weekday row on screen*. §9.377's
                    * printed week draws the same word in its own badge, so an unscoped
                    * `getByText('Pondelok', { exact: true })` matches two elements and fails on
                    * strict mode - which is what it did (`agos-datatable-renders-every-row-twice`).
                    * The editor's copy is the one a person clicks. */}
                  <p className="font-bold text-sm text-foreground" data-editor-day={day.dayKey}>
                    {day.dayName}
                  </p>
                  {week.startDate && (
                    <span className="text-xs text-muted-foreground">
                      {dayShortDate(week.startDate, day.dayKey)}
                    </span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">
                  {day.meals.length} <T>jedál</T>
                </span>
              </div>

              {day.enabled && (
                <div className="p-4 space-y-3">
                  {day.meals.map((meal, mealIndex) => (
                    <div key={meal.id} className="flex flex-wrap items-center gap-2">
                      {/* §9.133 - the order decides the sequence on the customer's menu, the printed
                        * sheet and the lunch tab, and there was no way to change it at all. Disabled
                        * at the ends rather than hidden, so the control does not move under the
                        * cursor as rows are shuffled. */}
                      <div className="flex flex-col gap-0.5 shrink-0">
                        <button
                          type="button"
                          data-meal-up={meal.id}
                          aria-label={tt('Posunúť vyššie')}
                          disabled={mealIndex === 0}
                          onClick={() =>
                            patchDay(day.dayKey, { meals: moveMeal(day.meals, meal.id, -1) })
                          }
                          className="w-6 h-6 rounded bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                          <ChevronUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          data-meal-down={meal.id}
                          aria-label={tt('Posunúť nižšie')}
                          disabled={mealIndex === day.meals.length - 1}
                          onClick={() =>
                            patchDay(day.dayKey, { meals: moveMeal(day.meals, meal.id, 1) })
                          }
                          className="w-6 h-6 rounded bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <select
                        value={meal.type}
                        onChange={(event) => patchMeal(day.dayKey, meal.id, { type: event.target.value })}
                        aria-label={tt('Sekcia')}
                        className={cn(FIELD_CLASSES, 'w-40')}
                      >
                        {/* §9.315 - the eight built-ins and then the franchise's own. A meal already
                          * filed under a section that has since been deleted would otherwise have no
                          * option to show and the select would silently read as the first one. */}
                        {sectionOptions.map((section) => (
                          <option key={section.type} value={section.type}>
                            {section.label}
                          </option>
                        ))}
                        {sectionOptions.every((section) => section.type !== meal.type) && (
                          <option value={meal.type}>{sectionLabel(settings, meal.type)}</option>
                        )}
                      </select>
                      <input
                        value={meal.name}
                        onChange={(event) => patchMeal(day.dayKey, meal.id, { name: event.target.value })}
                        placeholder={tt('Názov jedla')}
                        data-meal-name
                        className={cn(FIELD_CLASSES, 'flex-1 min-w-[12rem]')}
                      />
                      <input
                        type="number"
                        step="0.10"
                        value={meal.price}
                        onChange={(event) =>
                          patchMeal(day.dayKey, meal.id, { price: Number(event.target.value) || 0 })
                        }
                        aria-label={tt('Cena')}
                        className={cn(FIELD_CLASSES, 'w-24')}
                      />
                      {/* §9.39 - a soup's own price alongside a main, which overrides the franchise
                        * default above. Offered on soups only: it is the one section where a dish is
                        * sold both ways, and a bundle price on a dessert would be a number nothing
                        * reads. Empty means *use the franchise default*. */}
                      {meal.type === 'soup' && (
                        <input
                          type="number"
                          step="0.10"
                          min="0"
                          value={meal.bundlePrice ?? ''}
                          placeholder={tt('k menu')}
                          onChange={(event) =>
                            patchMeal(day.dayKey, meal.id, {
                              bundlePrice:
                                event.target.value.trim() === ''
                                  ? undefined
                                  : Number(event.target.value) || 0,
                            })
                          }
                          aria-label={tt('Cena k menu')}
                          data-meal-bundle-price
                          className={cn(FIELD_CLASSES, 'w-24')}
                        />
                      )}

                      {/* §9.39, owner option A - the product this meal deducts stock against. The
                        * line still carries the meal's negative id and is priced from `lunch_config`,
                        * so linking a product cannot change what the customer pays; the link rides in
                        * the order line's `parts` (§10A.14). */}
                      <select
                        value={meal.productId ?? ''}
                        onChange={(event) =>
                          patchMeal(day.dayKey, meal.id, {
                            productId:
                              event.target.value === '' ? undefined : Number(event.target.value),
                          })
                        }
                        aria-label={tt('Prepojený produkt')}
                        data-meal-product
                        className={cn(FIELD_CLASSES, 'w-40')}
                      >
                        <option value="">{tt('- bez skladu -')}</option>
                        {linkableProducts.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.name}
                          </option>
                        ))}
                      </select>

                      {/* Said, not refused. A menu price is *supposed* to undercut the à-la-carte one
                        * — that is what a daily menu is — so a difference is the normal case and
                        * refusing it would make the link unusable. The size of it is what tells a
                        * discount from a typo. */}
                      {priceGapNote(meal, linkableProducts) !== null && (
                        <p
                          className="w-full text-[11px] text-amber-500"
                          data-meal-price-gap={meal.id}
                        >
                          {priceGapNote(meal, linkableProducts)}
                        </p>
                      )}

                      {/* §9.401 - the popis this meal shows, read from the linked product. The
                        * editor never displayed one at all, so there was no way to check that
                        * linking the right product had fixed the sentence. */}
                      {lunchMealDescription(meal, linkableProducts) !== '' && (
                        <p
                          className="w-full text-[11px] text-muted-foreground italic"
                          data-meal-descr={meal.id}
                        >
                          {lunchMealDescription(meal, linkableProducts)}
                        </p>
                      )}

                      {/* §9.401 - the one place the stored copy is still read, and it is read to
                        * warn rather than to print. 45 of the 45 meals on the owner's restore carry
                        * a pasted description and none is linked, so without this the rule reads as
                        * *my descriptions were deleted*. It disappears as soon as a product is
                        * picked. */}
                      {meal.productId === undefined &&
                        (meal.description ?? '').trim() !== '' && (
                          <p
                            className="w-full text-[11px] text-amber-500"
                            data-meal-descr-orphan={meal.id}
                          >
                            {tt('Popis sa teraz berie z prepojeného produktu - vyberte ho vyššie.')}
                          </p>
                        )}

                      {/* §9.39 - *zoznam alergénov je odpad*. This was a `select multiple` 128px wide
                        * and one-and-a-half rows tall, holding fourteen options and needing a
                        * ctrl-click. Chips are how an allergen is printed and how anybody says it —
                        * 1, 7, 12 — and what is chosen stays legible. */}
                      <div
                        className="flex flex-wrap gap-1 max-w-[16rem]"
                        role="group"
                        aria-label={tt('Alergény')}
                        data-meal-allergens
                      >
                        {LUNCH_ALLERGENS.map((allergen) => {
                          const chosen = meal.allergens.includes(allergen.id)
                          return (
                            <button
                              key={allergen.id}
                              type="button"
                              data-allergen={allergen.id}
                              aria-pressed={chosen}
                              title={allergen.name}
                              onClick={() =>
                                patchMeal(day.dayKey, meal.id, {
                                  allergens: chosen
                                    ? meal.allergens.filter((id) => id !== allergen.id)
                                    : [...meal.allergens, allergen.id],
                                })
                              }
                              className={cn(
                                'w-6 h-6 rounded-md border text-[11px] font-bold tabular-nums transition-colors',
                                chosen
                                  ? 'bg-primary/15 border-primary text-primary'
                                  : 'bg-card border-border text-muted-foreground hover:border-primary/40',
                              )}
                            >
                              {allergen.id}
                            </button>
                          )
                        })}
                      </div>
                      <span className="text-xs text-muted-foreground w-16 text-right">
                        {formatMoney(meal.price)}
                      </span>
                      <button
                        type="button"
                        aria-label={tt('Odstrániť jedlo')}
                        onClick={() =>
                          patchDay(day.dayKey, {
                            meals: day.meals.filter((entry) => entry.id !== meal.id),
                          })
                        }
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}

                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {sectionOptions.map((section) => (
                      <button
                        key={section.type}
                        type="button"
                        data-add-meal={section.type}
                        onClick={() =>
                          patchDay(day.dayKey, {
                            meals: [...day.meals, newMeal(section.type, section.defaultPrice)],
                          })
                        }
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-muted text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/70 transition-colors"
                      >
                        <Plus className="w-3 h-3" /> {section.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <ConfirmDialog
          open={pendingWeekDeletion !== null}
          onClose={() => setPendingWeekDeletion(null)}
          onConfirm={() => {
            setWeeks((current) => current.filter((entry) => entry.weekId !== pendingWeekDeletion?.weekId))
            setWeekIndex(0)
            setPendingWeekDeletion(null)
          }}
          variant="danger"
          title={tt('Zmazať týždeň?')}
          description={`${tt('Týždeň')} ${pendingWeekDeletion?.label ?? ''} ${tt('aj so všetkými jedlami. Zmena sa uloží až po kliknutí na Uložiť.')}`}
          confirmLabel={tt('Zmazať')}
        />

        {/* §9.315 - legacy deletes a section's dishes without asking. The count is across every week,
          * because that is what the click removes. */}
        <ConfirmDialog
          open={pendingSectionDeletion !== null}
          onClose={() => setPendingSectionDeletion(null)}
          onConfirm={() => {
            if (pendingSectionDeletion !== null) removeCustomSection(pendingSectionDeletion)
            setPendingSectionDeletion(null)
          }}
          variant="danger"
          title={tt('Odstrániť sekciu?')}
          description={`${sectionLabel(settings, pendingSectionDeletion ?? '')} - ${tt('odstráni sa aj')} ${mealsInSection(pendingSectionDeletion ?? '')} ${tt('jedál vo všetkých týždňoch. Zmena sa uloží až po kliknutí na Uložiť.')}`}
          confirmLabel={tt('Odstrániť')}
        />
      </div>

      <LunchPrintView
        week={week}
        settings={settings}
        mode={printMode}
        dayKey={dayKeyOf(new Date())}
        products={linkableProducts}
      />
    </div>
  )
}

export { ALL_WEEKDAYS }
